import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Beer,
  Building2,
  Check,
  CircleAlert,
  Coffee,
  Compass,
  Copy,
  Dumbbell,
  Flame,
  Info,
  Loader2,
  Plane,
  Share2,
  ShieldAlert,
  Sparkles,
  Target,
  PlaneTakeoff,
  Wallet,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { GoodSalaryReport } from '../../types/goodSalary';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const verdictTone = (key: string) => {
  if (key === 'great') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (key === 'good') return 'bg-sky-100 text-sky-700 border-sky-200';
  if (key === 'tight') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-amber-100 text-amber-700 border-amber-200';
};

const GoodSalaryCountyPage: React.FC = () => {
  const { goodSalarySlug } = useParams<{ goodSalarySlug: string }>();
  const [data, setData] = useState<GoodSalaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedCardIndex, setCopiedCardIndex] = useState<number | null>(null);

  const parsed = useMemo(() => {
    const slug = String(goodSalarySlug || '').toLowerCase();
    const match = slug.match(/^is-(\d+)-a-good-salary-in-([a-z-]+)$/);
    if (!match) return null;
    return {
      slug,
      salary: Number(match[1]),
      county: match[2],
    };
  }, [goodSalarySlug]);

  useEffect(() => {
    if (!parsed?.salary || !parsed?.county) {
      setError('Salary or county route parameters are missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getGoodSalaryReport?slug=${encodeURIComponent(
            parsed.slug
          )}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError('Salary report not found for this county.');
          return;
        }
        setData(result.data as GoodSalaryReport);
      } catch (e) {
        setError('Unable to load salary report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [parsed]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
        <p className="font-bold text-slate-500">Loading salary verdict...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-3">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/salary"
            className="inline-block rounded-2xl bg-emerald-600 text-white px-8 py-4 font-black hover:bg-emerald-700"
          >
            Browse Salary Reports
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/salary/${data.slug}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const brandLine =
    'RentSmart.ie helps you find properties to rent in Ireland first, then compare salary reality, rent pressure, and what you can afford.';
  const shareText = `${formatCurrency(data.salary_value)} in ${data.county_name}: ${data.verdict.label} salary outlook. ${brandLine}`;
  const viralityData =
    data.ai_enrichment?.virality_data?.length ? data.ai_enrichment.virality_data : data.viral_baselines;
  const viralCardStyles = [
    'from-rose-50 to-white border-rose-200',
    'from-sky-50 to-white border-sky-200',
    'from-emerald-50 to-white border-emerald-200',
    'from-violet-50 to-white border-violet-200',
  ];

  const getViralIcon = (title: string) => {
    const normalized = title.toLowerCase();
    if (normalized.includes('pint')) return <Beer className="h-4 w-4" />;
    if (normalized.includes('trip')) return <Plane className="h-4 w-4" />;
    if (normalized.includes('coffee')) return <Coffee className="h-4 w-4" />;
    if (normalized.includes('gym')) return <Dumbbell className="h-4 w-4" />;
    return <Flame className="h-4 w-4" />;
  };

  const copyShareLine = async (shareLine: string, idx: number) => {
    const brandedShareLine = `${shareLine} ${brandLine}`;
    try {
      await navigator.clipboard.writeText(brandedShareLine);
      setCopiedCardIndex(idx);
      setTimeout(() => setCopiedCardIndex(null), 1400);
    } catch {
      // no-op if clipboard is blocked
    }
  };
  const guidanceCards = data.ai_enrichment
    ? [
        {
          key: 'affordability',
          title: 'Affordability Levers',
          subtitle: 'Small tweaks that can noticeably improve monthly breathing room.',
          items: data.ai_enrichment.affordability_levers,
          wrapperClass:
            'rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white px-5 py-5 shadow-sm',
          itemClass: 'rounded-lg bg-white border border-emerald-100 px-3 py-2',
          titleClass: 'text-emerald-700',
          subtitleClass: 'text-emerald-700/80',
          icon: <Target className="h-4 w-4" />,
        },
        {
          key: 'relocation',
          title: 'Relocation Playbook',
          subtitle: 'Practical next steps if you are moving for work or lifestyle.',
          items: data.ai_enrichment.relocation_playbook,
          wrapperClass:
            'rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white px-5 py-5 shadow-sm',
          itemClass: 'rounded-lg bg-white border border-sky-100 px-3 py-2',
          titleClass: 'text-sky-700',
          subtitleClass: 'text-sky-700/80',
          icon: <PlaneTakeoff className="h-4 w-4" />,
        },
        {
          key: 'watchouts',
          title: 'Local Watchouts',
          subtitle: 'Cost traps and pressure points worth planning for in advance.',
          items: data.ai_enrichment.local_watchouts,
          wrapperClass:
            'rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white px-5 py-5 shadow-sm',
          itemClass: 'rounded-lg bg-white border border-rose-100 px-3 py-2',
          titleClass: 'text-rose-700',
          subtitleClass: 'text-rose-700/80',
          icon: <ShieldAlert className="h-4 w-4" />,
        },
        {
          key: 'lifestyle-fit',
          title: 'Likely Affordable Lifestyles',
          subtitle: 'Lifestyle patterns that are typically realistic on this income profile.',
          items: data.ai_enrichment.likely_affordable_lifestyles,
          wrapperClass:
            'rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white px-5 py-5 shadow-sm',
          itemClass: 'rounded-lg bg-white border border-violet-100 px-3 py-2',
          titleClass: 'text-violet-700',
          subtitleClass: 'text-violet-700/80',
          icon: <Sparkles className="h-4 w-4" />,
        },
      ]
    : [];

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-16 px-4 relative overflow-hidden">
        <div className="absolute -top-8 -right-12 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-16 -left-10 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
            {data.tax_year} Is This a Good Salary Checker
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">{data.seo_content.h1}</h1>
          <p className="text-slate-300 max-w-4xl text-lg leading-relaxed">{data.verdict.statement}</p>
          <div className="mt-4 max-w-4xl rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
            <p className="text-sm text-slate-200 font-semibold leading-relaxed">
              {data.seo_content.snippet_hook}
            </p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-slate-400">
              <Share2 className="h-3.5 w-3.5" /> Share
            </span>
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={30} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareText}>
              <TwitterIcon size={30} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={30} round />
            </WhatsappShareButton>
            <LinkedinShareButton url={shareUrl} title={data.seo_content.h1} summary={shareText} source="RentSmart.ie">
              <LinkedinIcon size={30} round />
            </LinkedinShareButton>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 -mt-8 pb-16">
        <section className="grid lg:grid-cols-12 gap-8 mt-16">
          <div className="lg:col-span-8 space-y-8">
            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <span
                  className={`inline-flex px-3 py-1 rounded-full border text-xs font-black uppercase tracking-wider ${verdictTone(
                    data.verdict.key
                  )}`}
                >
                  {data.verdict.label}
                </span>
                <span className="text-xs font-black uppercase tracking-wider text-slate-400">
                  Top {data.verdict.top_percent_estimate}% signal
                </span>
              </div>

              <h2 className="text-2xl font-black text-slate-900 mb-4">Take-Home Breakdown</h2>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Gross Annual
                  </p>
                  <p className="text-xl font-black text-slate-900">{formatCurrency(data.salary_value)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-emerald-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">
                    Net Monthly
                  </p>
                  <p className="text-xl font-black text-emerald-800">
                    {formatCurrency(data.calculations.net_monthly)}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['Income Tax (PAYE)', data.calculations.paye],
                  ['USC', data.calculations.usc],
                  ['PRSI', data.calculations.prsi],
                  ['Total Deductions', data.calculations.total_deductions],
                  ['Net Annual', data.calculations.net_annual],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                      {label}
                    </p>
                    <p className="text-lg font-black text-slate-900">{formatCurrency(Number(value))}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Rent Realism in {data.county_name}</h2>
              <p className="text-slate-700 leading-relaxed mb-4">{data.sections.rent_realism}</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Median 1-Bed Rent
                  </p>
                  <p className="text-lg font-black text-slate-900">
                    {formatCurrency(data.county_costs.median_rent_1bed)}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Essentials
                  </p>
                  <p className="text-lg font-black text-slate-900">
                    {formatCurrency(data.county_costs.essentials_monthly)}
                  </p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">
                    Disposable
                  </p>
                  <p className="text-lg font-black text-emerald-800">
                    {formatCurrency(data.county_costs.disposable_after_rent_and_essentials)}
                  </p>
                </div>
              </div>
              {data.ai_enrichment?.rent_realism ? (
                <div className="mt-5 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 shadow-sm">
                  <p className="text-[10px] uppercase tracking-wider font-black text-sky-700 mb-2">
                    Deep Rent Insight
                  </p>
                  <p className="text-sm text-sky-900/90 leading-relaxed font-semibold">
                    {data.ai_enrichment.rent_realism}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Mortgage & Deposit Reality</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                A practical borrowing view for this salary level, before lender-specific checks.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Max Mortgage (4x)
                  </p>
                  <p className="text-xl font-black text-slate-900">
                    {formatCurrency(data.affordability_metrics.max_mortgage)}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                    Est. Deposit Needed
                  </p>
                  <p className="text-xl font-black text-slate-900">
                    {formatCurrency(data.affordability_metrics.estimated_deposit_needed)}
                  </p>
                </div>
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-sky-700 mb-1">
                    Total Buy Budget
                  </p>
                  <p className="text-xl font-black text-sky-900">
                    {formatCurrency(data.affordability_metrics.max_buy_budget)}
                  </p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700 mb-1">
                    Rent Target (35%)
                  </p>
                  <p className="text-xl font-black text-emerald-900">
                    {formatCurrency(data.affordability_metrics.recommended_rent)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">Lifestyle Context</h2>
              <p className="text-slate-700 leading-relaxed mb-4">{data.sections.lifestyle_comparison}</p>
              <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                {data.sections.lifestyle_signals.map((item, idx) => (
                  <li key={`${item}-${idx}`}>• {item}</li>
                ))}
              </ul>
              {data.ai_enrichment?.lifestyle_comparison ? (
                <p className="text-sm text-slate-600 leading-relaxed mt-4">
                  {data.ai_enrichment.lifestyle_comparison}
                </p>
              ) : null}
            </div>

            {viralityData?.length ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="h-5 w-5 text-rose-500" />
                  <h2 className="text-2xl font-black text-slate-900">Lifestyle Buying Power Snapshot</h2>
                </div>
                <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                  Handy, shareable Irish examples of what this salary might stretch to in day-to-day life.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {viralityData.map((item, idx) => {
                    const brandedShareLine = `${item.share_line} ${brandLine}`;
                    return (
                    <div
                      key={`${item.title}-${idx}`}
                      className={`rounded-2xl border bg-gradient-to-br p-5 hover:-translate-y-1 hover:shadow-lg transition-all ${
                        viralCardStyles[idx % viralCardStyles.length]
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-wider font-black text-slate-600">
                          {getViralIcon(item.title)}
                          {item.title}
                        </div>
                        <span className="inline-flex rounded-full bg-slate-900 text-white px-2 py-1 text-[10px] font-black uppercase tracking-wider">
                          Shareable
                        </span>
                      </div>

                      <p className="text-2xl font-black text-slate-900 mb-2">{item.value}</p>
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">{item.context}</p>

                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 mb-3">
                        <p className="text-[10px] uppercase tracking-wider font-black text-slate-500 mb-1">
                          Quick share line
                        </p>
                        <p className="text-xs text-slate-600 font-semibold">{item.share_line}</p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => copyShareLine(item.share_line, idx)}
                          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-wider transition-all ${
                            copiedCardIndex === idx
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                          }`}
                        >
                          {copiedCardIndex === idx ? (
                            <>
                              <Check className="h-3.5 w-3.5" /> Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" /> Copy share line
                            </>
                          )}
                        </button>
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                          <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                            <FacebookIcon size={24} round />
                          </FacebookShareButton>
                          <TwitterShareButton url={shareUrl} title={brandedShareLine}>
                            <TwitterIcon size={24} round />
                          </TwitterShareButton>
                          <WhatsappShareButton url={shareUrl} title={brandedShareLine}>
                            <WhatsappIcon size={24} round />
                          </WhatsappShareButton>
                          <LinkedinShareButton
                            url={shareUrl}
                            title={item.title}
                            summary={brandedShareLine}
                            source="RentSmart.ie"
                          >
                            <LinkedinIcon size={24} round />
                          </LinkedinShareButton>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {data.ai_enrichment ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm space-y-5">
                <h2 className="text-2xl font-black text-slate-900">Local Guidance</h2>
                <p className="text-slate-700 leading-relaxed">{data.ai_enrichment.verdict_context}</p>
                <p className="text-slate-700 leading-relaxed">{data.ai_enrichment.county_vs_dublin}</p>
                <p className="text-slate-700 leading-relaxed">{data.ai_enrichment.salary_rank_context}</p>
              </div>
            ) : null}

            {guidanceCards.length ? (
              <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-black text-slate-900 mb-2">Action Board</h2>
                <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                  Each list is split into practical actions, risks, and likely lifestyle outcomes to make
                  decisions faster.
                </p>
                <div className="grid md:grid-cols-2 gap-5">
                  {guidanceCards.map((card) => (
                    <div key={card.key} className={card.wrapperClass}>
                      <div className={`inline-flex items-center gap-2 mb-3 ${card.titleClass}`}>
                        {card.icon}
                        <p className="text-xs font-black uppercase tracking-wider">{card.title}</p>
                      </div>
                      <p className={`text-xs font-semibold mb-3 ${card.subtitleClass}`}>{card.subtitle}</p>
                      <ul className="space-y-2 text-sm text-slate-700 font-semibold">
                        {card.items.map((item, idx) => (
                          <li key={`${card.key}-${idx}`} className={card.itemClass}>
                            <span className="mr-2 text-slate-400">{idx + 1}.</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <PseoEmailCapture
              pageType="good-salary"
              pageSlug={data.slug}
              locationName={data.county_name}
              contextHeadline={`Get Salary Reality Updates In ${data.county_name}`}
              contextReason={`Receive Irish market updates tailored to this salary profile in ${data.county_name}, with renter/buyer affordability changes and new local salary-reality report updates.`}
            />

            <div className="rounded-[2rem] border border-slate-100 bg-white p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black text-slate-900 mb-4">FAQ</h2>
              <div className="space-y-3">
                {data.seo_content.faq.map((item, idx) => (
                  <details
                    key={`${item.question}-${idx}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <summary className="cursor-pointer font-bold text-slate-800">{item.question}</summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-8 space-y-6">
              <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-2 flex items-center gap-2">
                  <Wallet className="h-5 w-5" /> Rentals You Can Actually Target
                </h3>
                <p className="text-sm text-emerald-100 mb-5 leading-relaxed">
                  Jump straight to listings in {data.county_name} that align with this income profile.
                </p>
                <a
                  href={data.ctas.rent.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white px-4 py-3 font-black text-emerald-700"
                >
                  {data.ctas.rent.label} <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-sky-700 to-sky-900 p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-2 flex items-center gap-2">
                  <Building2 className="h-5 w-5" /> Homes Within Reach
                </h3>
                <p className="text-sm text-sky-100 mb-5 leading-relaxed">
                  Explore sales listings in {data.county_name} with a budget anchored to this salary.
                </p>
                <a
                  href={data.ctas.buy.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white px-4 py-3 font-black text-sky-800"
                >
                  {data.ctas.buy.label} <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-black text-slate-900 mb-3 flex items-center gap-2">
                  <Compass className="h-5 w-5 text-emerald-600" /> Related Tool
                </h3>
                <Link
                  to={data.ctas.salary_after_tax.path}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-700"
                >
                  {data.ctas.salary_after_tax.label} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <Link
                to="/salary"
                className="no-underline inline-flex w-full items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500 hover:border-emerald-500 hover:text-emerald-700"
              >
                Check Another Salary
              </Link>
            </div>
          </aside>
        </section>

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-700 mt-0.5" />
            <p className="text-sm text-amber-900/90 font-semibold leading-relaxed">
              Figures are directional estimates. RentSmart does not provide financial, tax, legal,
              mortgage or investment advice. Validate decisions with qualified professionals.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default GoodSalaryCountyPage;
